// Maps logical sound cues to asset URLs (served from /public/sounds). The audio
// adapter loads these; if a file is missing it synthesises a fallback tone, so
// the game is never silently broken. A null url means "always synthesize" —
// handy for cues that don't have a recorded asset yet. (The legacy sound.js was
// fully written but never wired in — this manifest is what makes audio a
// one-line cue: Audio.play('hit').)
export type SoundCue =
  | 'shoot'
  | 'hit'
  | 'miss'
  | 'increase'
  | 'decrease'
  | 'explode'
  | 'levelup'
  | 'combo'
  | 'comboLost'
  | 'powerup';

export interface SoundDef {
  /** Asset URL, or null to go straight to the synthesized fallback. */
  url: string | null;
  volume: number;
  /** Oscillator fallback if the file fails to load (or url is null). */
  fallback: { type: OscillatorType; freq: number; duration: number; sweep: number };
}

export const AUDIO_MANIFEST: Record<SoundCue, SoundDef> = {
  shoot: { url: '/sounds/shoot.mp3', volume: 0.7, fallback: { type: 'sawtooth', freq: 880, duration: 0.15, sweep: 100 } },
  hit: { url: '/sounds/hit.mp3', volume: 1.0, fallback: { type: 'sine', freq: 660, duration: 0.3, sweep: 0 } },
  miss: { url: '/sounds/miss.mp3', volume: 0.8, fallback: { type: 'triangle', freq: 220, duration: 0.4, sweep: 0 } },
  increase: { url: '/sounds/increase.mp3', volume: 0.6, fallback: { type: 'sine', freq: 440, duration: 0.1, sweep: 220 } },
  decrease: { url: '/sounds/decrease.mp3', volume: 0.6, fallback: { type: 'sine', freq: 660, duration: 0.1, sweep: -220 } },
  explode: { url: '/sounds/explode.mp3', volume: 1.0, fallback: { type: 'sawtooth', freq: 100, duration: 0.5, sweep: 50 } },
  // Rising chime for level-up; synth-only until a recorded asset lands.
  levelup: { url: null, volume: 0.8, fallback: { type: 'sine', freq: 523, duration: 0.35, sweep: 392 } },
  // Combo tier-up stinger / streak-lost thud / power-up sparkle — synth-only.
  combo: { url: null, volume: 0.7, fallback: { type: 'square', freq: 660, duration: 0.22, sweep: 660 } },
  comboLost: { url: null, volume: 0.5, fallback: { type: 'triangle', freq: 180, duration: 0.3, sweep: -80 } },
  powerup: { url: null, volume: 0.8, fallback: { type: 'sine', freq: 784, duration: 0.3, sweep: 524 } },
};

// Musical destruction: each digit destroyed plays its own note, so kill
// streaks form melodies. Semitone offsets over a two-octave major pentatonic —
// applied as a playback-rate multiplier on the 'hit' cue.
export const DIGIT_SEMITONES: readonly number[] = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21];

export function digitPlaybackRate(digit: number): number {
  const semi = DIGIT_SEMITONES[digit] ?? 0;
  // Centered around the scale's middle so low digits aren't muddy.
  return Math.pow(2, (semi - 9) / 12);
}
